import type { Format, FormEntry, PlayerFormatStats } from '@foosball/shared';
import { formatDistanceToNow } from 'date-fns';
import { lazy, Suspense, useState } from 'react';
import { useParams } from 'react-router';

import { AchievementShelf } from '../components/AchievementShelf';
import { Card } from '../components/ui';
import {
  useAchievementCatalogue,
  useHeadToHead,
  usePlayerProfile,
  usePlayers,
  useRatingHistory,
} from '../hooks/useMatches';
import { useAuth } from '../lib/auth-context';

/**
 * Recharts is ~370 kB — more than the rest of the app put together — and only
 * the profile page uses it. Loading it lazily keeps it out of the critical
 * path for the primary journey (submitting a result on a phone at the table).
 */
const RatingChart = lazy(() =>
  import('../components/RatingChart').then((m) => ({ default: m.RatingChart })),
);

const chartFallback = <p className="py-8 text-center text-sm text-slate-500">Loading chart…</p>;

function streakLabel(streak: number): string {
  if (streak === 0) return '—';
  return streak > 0 ? `W${streak}` : `L${Math.abs(streak)}`;
}

function StatBlock({ stats }: { stats: PlayerFormatStats }) {
  return (
    <Card>
      <div className="flex items-baseline justify-between">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
          {stats.format}
        </h3>
        <span className="text-2xl font-bold tabular-nums">{stats.rating}</span>
      </div>

      <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
        <Stat label="Played" value={stats.played} />
        <Stat label="Won" value={stats.won} />
        <Stat label="Lost" value={stats.lost} />
        <Stat
          label="Win rate"
          value={stats.winRate === null ? '—' : `${Math.round(stats.winRate * 100)}%`}
        />
        <Stat label="Peak" value={stats.peakRating} />
        <Stat label="Streak" value={streakLabel(stats.streak)} />
      </dl>
    </Card>
  );
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <>
      <dt className="text-slate-500 dark:text-slate-400">{label}</dt>
      <dd className="text-right font-medium tabular-nums">{value}</dd>
    </>
  );
}

function FormPip({ entry }: { entry: FormEntry }) {
  const opponents = entry.opponents.join(' & ');
  return (
    <li
      title={`${entry.won ? 'Won' : 'Lost'} ${entry.scoreFor}–${entry.scoreAgainst} v ${opponents}`}
      className={`flex size-7 items-center justify-center rounded-full text-xs font-bold text-white ${
        entry.won ? 'bg-emerald-600' : 'bg-red-500'
      }`}
    >
      {entry.won ? 'W' : 'L'}
      <span className="sr-only">
        {entry.won ? 'Won' : 'Lost'} {entry.scoreFor}–{entry.scoreAgainst} against {opponents}
      </span>
    </li>
  );
}

export function ProfilePage() {
  const params = useParams();
  const { user } = useAuth();
  const playerId = Number(params.id);

  const profile = usePlayerProfile(Number.isFinite(playerId) ? playerId : null);
  const players = usePlayers();
  const catalogue = useAchievementCatalogue();

  const [chartFormat, setChartFormat] = useState<Format>('1v1');
  const [opponentId, setOpponentId] = useState<number | null>(null);

  const history = useRatingHistory(Number.isFinite(playerId) ? playerId : null, chartFormat);
  const h2h = useHeadToHead(Number.isFinite(playerId) ? playerId : null, opponentId);

  if (profile.isPending) {
    return <p className="text-sm text-slate-500">Loading…</p>;
  }

  if (profile.isError || !profile.data) {
    return (
      <Card>
        <p className="text-sm text-slate-500 dark:text-slate-400">That player could not be found.</p>
      </Card>
    );
  }

  const { player, stats, form, totals, joinedAt, achievements } = profile.data;
  const isMe = player.id === user?.id;

  return (
    <>
      <header className="mb-5">
        <h1 className="text-2xl font-bold tracking-tight">
          {player.displayName}
          {isMe && <span className="ml-2 text-base font-normal text-slate-400">(you)</span>}
        </h1>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          {totals.played} match{totals.played === 1 ? '' : 'es'} · {totals.won}W {totals.lost}L ·
          joined {formatDistanceToNow(new Date(joinedAt), { addSuffix: true })}
        </p>
      </header>

      <div className="mb-4 grid gap-4 sm:grid-cols-2">
        {stats.map((s) => (
          <StatBlock key={s.format} stats={s} />
        ))}
      </div>

      <Card className="mb-4">
        <div className="mb-3 flex items-center justify-between gap-3">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Rating history
          </h2>
          <div
            role="group"
            aria-label="Chart format"
            className="flex gap-1 rounded-lg bg-slate-100 p-0.5 dark:bg-slate-800"
          >
            {(['1v1', '2v2'] as const).map((option) => (
              <button
                key={option}
                type="button"
                aria-pressed={chartFormat === option}
                onClick={() => setChartFormat(option)}
                className={`rounded px-2.5 py-1 text-xs font-semibold ${
                  chartFormat === option
                    ? 'bg-white shadow-sm dark:bg-slate-950'
                    : 'text-slate-500 dark:text-slate-400'
                }`}
              >
                {option}
              </button>
            ))}
          </div>
        </div>

        {history.isPending ? (
          chartFallback
        ) : (
          <Suspense fallback={chartFallback}>
            <RatingChart points={history.data?.points ?? []} />
          </Suspense>
        )}
      </Card>

      <Card className="mb-4">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
          Achievements
        </h2>
        <AchievementShelf catalogue={catalogue.data ?? []} earned={achievements} />
      </Card>

      <Card className="mb-4">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
          Form guide
        </h2>
        {form.length === 0 ? (
          <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">No results yet.</p>
        ) : (
          <>
            <ul aria-label="Recent results" className="mt-3 flex flex-wrap gap-1.5">
              {form.map((entry) => (
                <FormPip key={entry.matchId} entry={entry} />
              ))}
            </ul>
            <ul className="mt-4 space-y-1.5 text-sm">
              {form.slice(0, 5).map((entry) => (
                <li key={entry.matchId} className="flex items-center justify-between gap-2">
                  <span className="truncate text-slate-600 dark:text-slate-300">
                    v {entry.opponents.join(' & ')}
                  </span>
                  <span className="shrink-0 tabular-nums">
                    {entry.scoreFor}–{entry.scoreAgainst}
                    {entry.ratingDelta !== null && (
                      <span
                        className={`ml-2 text-xs ${
                          entry.ratingDelta >= 0
                            ? 'text-emerald-600 dark:text-emerald-400'
                            : 'text-red-600 dark:text-red-400'
                        }`}
                      >
                        {entry.ratingDelta >= 0 ? '+' : ''}
                        {entry.ratingDelta}
                      </span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          </>
        )}
      </Card>

      <Card>
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
          Head to head
        </h2>

        <label className="mt-3 block">
          <span className="sr-only">Compare with</span>
          <select
            aria-label="Compare with"
            value={opponentId ?? ''}
            onChange={(e) => setOpponentId(e.target.value === '' ? null : Number(e.target.value))}
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-950"
          >
            <option value="">Choose an opponent…</option>
            {(players.data ?? [])
              .filter((p) => p.id !== player.id)
              .map((p) => (
                <option key={p.id} value={p.id}>
                  {p.displayName}
                </option>
              ))}
          </select>
        </label>

        {h2h.isSuccess && h2h.data && (
          <div className="mt-4">
            {h2h.data.totals.played === 0 ? (
              <p className="text-sm text-slate-500 dark:text-slate-400">
                {player.displayName} and {h2h.data.opponent.displayName} have not played each other
                yet.
              </p>
            ) : (
              <>
                <div className="flex items-center justify-center gap-4 text-center">
                  <div>
                    <div className="text-3xl font-bold tabular-nums text-emerald-600 dark:text-emerald-400">
                      {h2h.data.totals.won}
                    </div>
                    <div className="text-xs text-slate-500">{player.displayName}</div>
                  </div>
                  <span className="text-slate-300">–</span>
                  <div>
                    <div className="text-3xl font-bold tabular-nums text-red-500">
                      {h2h.data.totals.lost}
                    </div>
                    <div className="text-xs text-slate-500">{h2h.data.opponent.displayName}</div>
                  </div>
                </div>

                <p className="mt-3 text-center text-xs text-slate-500 dark:text-slate-400">
                  {h2h.data.totals.played} meeting{h2h.data.totals.played === 1 ? '' : 's'} ·{' '}
                  {h2h.data.totals.goalsFor}–{h2h.data.totals.goalsAgainst} on goals
                </p>
              </>
            )}
          </div>
        )}
      </Card>
    </>
  );
}
